import React, { useEffect, useState } from "react";
import axios from "../utils/axiosConfig";
import { useParams } from "react-router-dom";
import { Box, Button, Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, Select, MenuItem, Tabs, Tab } from "@mui/material";
import { TableSortLabel } from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useValidation } from '../context/ValidationContext';

import { toast } from '../utils/toast';
import { handleApiError } from '../utils/apiErrorHandler';

import { useRegistrationValidation } from '../hooks/useRegistrationValidation';
import { useRegistrationStatus } from '../hooks/useRegistrationStatus';
import RegistrationStatusAlert from '../components/RegistrationStatusAlert';

interface Athlete {
  _id: string;
  firstname: string;
  surname: string;
  age: number;
  gender: string;
}

interface CompetitionGroup {
  _id: string;
  name: string;
  type: "Elite" | "Basis";
  ageGroup?: {
    minYear?: number;
    maxYear?: number;
  };
  gender: string;
}

interface Registration {
  _id: string;
  athlete: Athlete;
  competitionGroup?: CompetitionGroup;
  status: string;
}

// New interfaces for waitlist functionality
interface WaitlistedAthlete {
  athleteId: string;
  firstname: string;
  surname: string;
  age: number;
  gender: string;
  competitionGroup: string;
  addedAt: string;
}

interface RegistrationStatus {
  eliteTotal: number;
  basicTotal: number;
  eliteLimit: number | null;
  basicLimit: number | null;
  eliteRemaining: number | null;
  basicRemaining: number | null;
  isEliteFull: boolean;
  isBasicFull: boolean;
}

interface WaitlistData {
  Elite: WaitlistedAthlete[];
  Basis: WaitlistedAthlete[];
}

const AthleteRegistrationPage: React.FC = () => {
  const { competitionId } = useParams();
  const { clubId }  = useAuth();
  const { refreshValidation } = useRegistrationValidation();
  
  // Use the new registration status hook
  const registrationStatus = useRegistrationStatus(competitionId);
    
  const [registeredAthletes, setRegisteredAthletes] = useState<Registration[]>([]);
  const [availableAthletes, setAvailableAthletes] = useState<Athlete[]>([]);
  const [competitionGroups, setCompetitionGroups] = useState<CompetitionGroup[]>([]);
  const [loading, setLoading] = useState(true);

  const [orderBy, setOrderBy] = useState<keyof Athlete>("surname");
  const [order, setOrder] = useState<"asc" | "desc">("asc");
  
  const [orderByAvailable, setOrderByAvailable] = useState<keyof Athlete>("surname");
  const [orderAvailable, setOrderAvailable] = useState<"asc" | "desc">("asc");

  const [registrationError, setRegistrationError] = useState<string | null>(null);

  // New states for waitlist functionality
  const [activeTab, setActiveTab] = useState<string>("register"); // 'register' or 'waitlist'
  const [waitlistTab, setWaitlistTab] = useState<"Elite" | "Basis">("Elite");
  const [waitlistedAthletes, setWaitlistedAthletes] = useState<WaitlistData>({
    Elite: [],
    Basis: []
  });
  const [competitionLimits, setCompetitionLimits] = useState<RegistrationStatus>({
    eliteTotal: 0,
    basicTotal: 0,
    eliteLimit: null,
    basicLimit: null,
    eliteRemaining: null,
    basicRemaining: null,
    isEliteFull: false,
    isBasicFull: false
  });

  const [onlyBasisAvailable, setOnlyBasisAvailable] = useState<boolean>(false);

  // 🔹 Daten für gemeldete & verfügbare Athleten abrufen
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Load registration status (limits and counts)
        const statusResponse = await axios.get(`/api/competitions/${competitionId}/registration-status`);
        setCompetitionLimits(statusResponse.data);

        // 🔹 Gemeldete Athleten abrufen
        const regResponse = await axios.get(`/api/registrations/${competitionId}/${clubId}`);
        setRegisteredAthletes(regResponse.data);
  
        // 🔹 Alle Wettkampfklassen des Wettkampfes laden
        const compResponse = await axios.get(`/api/competitions/${competitionId}`);
        setOnlyBasisAvailable(compResponse.data.onlyBasisRegistrationAvailable || false);
        setCompetitionGroups(compResponse.data.competitionGroups);

        // Waitlist data
        const waitlistResponse = await axios.get(`/api/waitlist/competition/${competitionId}/club/${clubId}`);
        setWaitlistedAthletes(waitlistResponse.data);

        // 🔹 Alle Athleten des Vereins abrufen
        const athResponse = await axios.get(`/api/athletes/club/${clubId}`);
        
        // Filter out both registered and waitlisted athletes
        const registeredIds = regResponse.data.map((r: Registration) => r.athlete._id);
        const waitlistedIds = [
          ...waitlistResponse.data.Elite.map((a: WaitlistedAthlete) => a.athleteId),
          ...waitlistResponse.data.Basis.map((a: WaitlistedAthlete) => a.athleteId)
        ];
        
        setAvailableAthletes(athResponse.data.filter(
          (ath: Athlete) => !registeredIds.includes(ath._id) && !waitlistedIds.includes(ath._id)
        ));

        refreshValidation();
        setLoading(false);
      } catch (error) {
        handleApiError(error, "Fehler beim Laden der Daten");
      }
    };
  
    fetchData();
  }, [competitionId, clubId]);
  

  // 🔹 Sportler anmelden
  const registerAthlete = async (athleteId: string, type: "Elite" | "Basis") => {
    try {
      // Fehlerstate zurücksetzen
      setRegistrationError(null);

      // Check if registration is closed
      if (registrationStatus.isClosed) {
        toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
        return;
      }

      // Check if competition is full for this type
      if ((type === "Elite" && competitionLimits.isEliteFull) ||
          (type === "Basis" && competitionLimits.isBasicFull)) {
        toast.info(`${type}-Kontingent ist voll. Bitte nutze die Warteliste.`);
        return;
      }

      const athlete = availableAthletes.find(a => a._id === athleteId);
  
      const matchingGroup = competitionGroups.find(g => {
        // Type check
        const typeMatch = g.type === type;
        
        // Gender check
        let genderMatch = false;
        const compGroupGender = g.gender;
        if (compGroupGender === "mixed") {
          genderMatch = true;
        } else {
          genderMatch = g.gender === athlete?.gender;
        }
        
        // Age checks - only if age group is defined
        let ageMatch = true;
        
        if (g.ageGroup) {
          // Min year check - only if minYear is defined
          if (g.ageGroup.minYear !== undefined && g.ageGroup.minYear !== null) {
            ageMatch = ageMatch && g.ageGroup.minYear <= athlete?.age!;
          }
          
          // Max year check - only if maxYear is defined
          if (g.ageGroup.maxYear !== undefined && g.ageGroup.maxYear !== null) {
            ageMatch = ageMatch && g.ageGroup.maxYear >= athlete?.age!;
          }
        }
        return typeMatch && genderMatch && ageMatch;
      });

      if (!matchingGroup) {
        toast.error("Keine passende Wettkampfklasse gefunden.");
        return;
      }

      // 🔹 API-Request ausführen & neue Registrierung erhalten
      const response = await axios.post(`/api/registrations`, { 
        competition: competitionId, 
        club: clubId, 
        athlete: athleteId,
        competitionGroup: matchingGroup._id 
      });
  
      // 🔹 Sportler in die Liste der gemeldeten Athleten aufnehmen (mit richtiger registrationId)
      setRegisteredAthletes([...registeredAthletes, response.data]);
  
      // 🔹 Sportler aus der Liste der noch nicht gemeldeten Athleten entfernen
      setAvailableAthletes(availableAthletes.filter(a => a._id !== athleteId));

      // Update registration status counts
      if (type === 'Elite') {
        setCompetitionLimits(prev => ({
          ...prev,
          eliteTotal: prev.eliteTotal + 1,
          eliteRemaining: prev.eliteRemaining !== null ? prev.eliteRemaining - 1 : null,
          isEliteFull: prev.eliteRemaining !== null && prev.eliteRemaining <= 1
        }));
      } else {
        setCompetitionLimits(prev => ({
          ...prev,
          basicTotal: prev.basicTotal + 1,
          basicRemaining: prev.basicRemaining !== null ? prev.basicRemaining - 1 : null,
          isBasicFull: prev.basicRemaining !== null && prev.basicRemaining <= 1
        }));
      }

      toast.success(`${athlete?.firstname} ${athlete?.surname} wurde erfolgreich gemeldet.`);
      refreshValidation();      
    } catch (error: any) {
      // Verbesserte Fehlerbehandlung      
      const errorCode = error.originalError?.response.data.error.code || '';
      const errorMessage = error.originalError?.response.data.error.message || 'Fehler beim Anmelden des Sportlers.';
      
      // Spezifische Behandlung für den MAX_NUMBER_REGISTRATIONS_REACHED Fehler
      if (errorCode === 'MAX_NUMBER_REGISTRATIONS_REACHED') {
        setRegistrationError(`Maximales Kontingent erreicht: ${errorMessage}`);
        toast.error(`${type}-Kontingent voll: ${errorMessage}`);
      } else {
        handleApiError(error, "Fehler beim Anmelden des Sportlers.");
      }
    }
  };

  // Add athlete to waitlist
  const addToWaitlist = async (athleteId: string, type: "Elite" | "Basis") => {
    try {
      // Check if registration is closed
      if (registrationStatus.isClosed) {
        toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
        return;
      }

      const athlete = availableAthletes.find(a => a._id === athleteId);
      if (!athlete) {
        toast.error("Sportler nicht gefunden.");
        return;
      }
      
      // Find matching group
      const matchingGroup = competitionGroups.find(g => {
        // Type check
        const typeMatch = g.type === type;
        
        // Gender check
        let genderMatch = false;
        const compGroupGender = g.gender;
        if (compGroupGender === "mixed") {
          genderMatch = true;
        } else {
          genderMatch = g.gender === athlete.gender;
        }
        
        // Age checks - only if age group is defined
        let ageMatch = true;
        
        if (g.ageGroup) {
          // Min year check - only if minYear is defined
          if (g.ageGroup.minYear !== undefined && g.ageGroup.minYear !== null) {
            ageMatch = ageMatch && g.ageGroup.minYear <= athlete.age;
          }
          
          // Max year check - only if maxYear is defined
          if (g.ageGroup.maxYear !== undefined && g.ageGroup.maxYear !== null) {
            ageMatch = ageMatch && g.ageGroup.maxYear >= athlete.age;
          }
        }
        return typeMatch && genderMatch && ageMatch;
      });

      if (!matchingGroup) {
        toast.error("Keine passende Wettkampfklasse gefunden.");
        return;
      }

      // API request to add to waitlist
      const response = await axios.post('/api/waitlist/add', {
        competitionId,
        athleteId,
        competitionGroup: matchingGroup._id,
        type
      });
      
      // Add to waitlisted athletes
      const newWaitlistedAthlete: WaitlistedAthlete = {
        athleteId,
        firstname: athlete.firstname,
        surname: athlete.surname,
        age: athlete.age,
        gender: athlete.gender,
        competitionGroup: matchingGroup._id,
        addedAt: new Date().toISOString()
      };
      
      setWaitlistedAthletes(prev => ({
        ...prev,
        [type]: [...prev[type], newWaitlistedAthlete]
      }));
      
      // Remove from available athletes
      setAvailableAthletes(availableAthletes.filter(a => a._id !== athleteId));
      
      toast.success(`${athlete.firstname} ${athlete.surname} wurde zur ${type}-Warteliste hinzugefügt.`);
    } catch (error) {
      handleApiError(error, "Fehler beim Hinzufügen zur Warteliste.");
    }
  };

  // Remove from waitlist
  const removeFromWaitlist = async (athleteId: string, type: "Elite" | "Basis") => {
    try {
      // Check if registration is closed
      if (registrationStatus.isClosed) {
        toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
        return;
      }

      const athlete = waitlistedAthletes[type].find(a => a.athleteId === athleteId);
      if (!athlete) {
        toast.error("Sportler nicht auf der Warteliste gefunden.");
        return;
      }
      
      // API request to remove from waitlist
      await axios.delete(`/api/waitlist/${competitionId}/athlete/${athleteId}`);
      
      // Get the original athlete data
      const athleteResponse = await axios.get(`/api/athletes/${athleteId}`);
      const originalAthlete = athleteResponse.data;
      
      // Add back to available athletes
      setAvailableAthletes([...availableAthletes, originalAthlete]);
      
      // Remove from waitlisted athletes
      setWaitlistedAthletes(prev => ({
        ...prev,
        [type]: prev[type].filter(a => a.athleteId !== athleteId)
      }));
      
      toast.success(`${athlete.firstname} ${athlete.surname} wurde von der Warteliste entfernt.`);
    } catch (error) {
      handleApiError(error, "Fehler beim Entfernen von der Warteliste.");
    }
  };
  

  // 🔹 Sportler abmelden
  const unregisterAthlete = async (registrationId: string) => {
    try {
      // Check if registration is closed
      if (registrationStatus.isClosed) {
        toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
        return;
      }

      await axios.delete(`/api/registrations/${registrationId}`);
      const registration = registeredAthletes.find(a => a._id === registrationId);
      
      if (registration) {
        const athlete = registration.athlete;
        const type = registration.competitionGroup?.type || "Basis";
        
        // Update registration counts
        if (type === 'Elite') {
          setCompetitionLimits(prev => ({
            ...prev,
            eliteTotal: prev.eliteTotal - 1,
            eliteRemaining: prev.eliteRemaining !== null ? prev.eliteRemaining + 1 : null,
            isEliteFull: false
          }));
        } else {
          setCompetitionLimits(prev => ({
            ...prev,
            basicTotal: prev.basicTotal - 1,
            basicRemaining: prev.basicRemaining !== null ? prev.basicRemaining + 1 : null,
            isBasicFull: false
          }));
        }
        
        // Add back to available athletes
        setAvailableAthletes([...availableAthletes, athlete]);
      }
      
      setRegisteredAthletes(registeredAthletes.filter(a => a._id !== registrationId));
      refreshValidation();
    } catch (error: any) {
      handleApiError(error, "Fehler beim Abmelden des Sportlers.");
    }
  };

  // 🔹 Wettkampfklasse ändern
  const updateCompetitionGroup = async (registrationId: string, competitionGroupId: string) => {
    try {
      // Check if registration is closed
      if (registrationStatus.isClosed) {
        toast.error('Die Meldung ist geschlossen. Änderungen sind nicht mehr möglich.');
        return;
      }

      await axios.put(`/api/registrations/${registrationId}`, { competitionGroup: competitionGroupId });
      setRegisteredAthletes(registeredAthletes.map(a => a._id === registrationId ? { ...a, competitionGroup: competitionGroups.find(g => g._id === competitionGroupId) } : a));
      refreshValidation();
    } catch (error: any) {
      
      // Verbesserte Fehlerbehandlung      
      const errorCode = error.originalError?.response.data.error.code || '';
      const errorMessage = error.originalError?.response.data.error.message || 'Fehler beim Anmelden des Sportlers.';
      
      // Spezifische Behandlung für den MAX_NUMBER_REGISTRATIONS_REACHED Fehler
      if (errorCode === 'MAX_NUMBER_REGISTRATIONS_REACHED') {
        setRegistrationError(`Maximales Kontingent erreicht: ${errorMessage}`);
        toast.error(`Kontingent voll: ${errorMessage}`);
      } else {
        handleApiError(error, "Fehler beim Anmelden des Sportlers.");
      }
    }
  };

  const handleSort = (property: keyof Athlete) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSortAvailable = (property: keyof Athlete) => {
    const isAsc = orderByAvailable === property && orderAvailable === "asc";
    setOrderAvailable(isAsc ? "desc" : "asc");
    setOrderByAvailable(property);
  };
  
  const sortedRegisteredAthletes = [...registeredAthletes].sort((a, b) => {
    if (a.athlete[orderBy] < b.athlete[orderBy]) return order === "asc" ? -1 : 1;
    if (a.athlete[orderBy] > b.athlete[orderBy]) return order === "asc" ? 1 : -1;
    return 0;
  });
  
  const sortedAvailableAthletes = [...availableAthletes].sort((a, b) => {
    if (a[orderByAvailable] < b[orderByAvailable]) return orderAvailable === "asc" ? -1 : 1;
    if (a[orderByAvailable] > b[orderByAvailable]) return orderAvailable === "asc" ? 1 : -1;
    return 0;
  });

  // Find competition group name by ID
  const getGroupName = (groupId: string): string => {
    const group = competitionGroups.find(g => g._id === groupId);
    return group ? group.name : "Unbekannt";
  };

  return (
    <Container component="main" maxWidth="lg">
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Typography variant="h5" gutterBottom>
          Sportler-Meldung
        </Typography>

        {/* Registration Status Alert */}
        <RegistrationStatusAlert competitionId={competitionId || ''} />

        {/* Registration status warning - only shown when a category is full */}
        {(competitionLimits.isEliteFull || competitionLimits.isBasicFull) && (
          <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              Hinweis zu Kontingentbegrenzungen:
            </Typography>
            <Box sx={{ mt: 1 }}>
              {competitionLimits.isEliteFull && (
                <Typography>
                  ⚠️ Elite-Kategorie ist ausgebucht! Athleten können nur noch der Warteliste hinzugefügt werden.
                </Typography>
              )}
              {competitionLimits.isBasicFull && (
                <Typography>
                  ⚠️ Basis-Kategorie ist ausgebucht! Athleten können nur noch der Warteliste hinzugefügt werden.
                </Typography>
              )}
            </Box>
          </Box>
        )}

        {/* Fehleranzeige */}
        {registrationError && (
          <Box 
            sx={{ 
              mt: 2, 
              mb: 2, 
              p: 2, 
              backgroundColor: 'error.light', 
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <Box component="span" sx={{ display: 'flex', mr: 1 }}>
              ⚠️
            </Box>
            <Typography color="error.dark" variant="body1">
              {registrationError}
            </Typography>
          </Box>
        )}

        {/* Tabs for register/waitlist */}
        <Tabs 
          value={activeTab} 
          onChange={(_, newValue) => setActiveTab(newValue)}
          sx={{ mt: 2, mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Sportler melden" value="register" />
          <Tab label="Warteliste" value="waitlist" />
        </Tabs>

        {loading ? (
          <Typography>Lade Daten...</Typography>
        ) : (
          <>
            {activeTab === "register" && (
              <>
                {/* Gemeldete Sportler */}
                <Typography variant="h6" sx={{ mt: 3 }}>Gemeldete Sportler</Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Status</TableCell>
                        <TableCell>
                          <TableSortLabel active={orderBy === "firstname"} direction={order} onClick={() => handleSort("firstname")}>
                            Vorname
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={orderBy === "surname"} direction={order} onClick={() => handleSort("surname")}>
                            Nachname
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={orderBy === "age"} direction={order} onClick={() => handleSort("age")}>
                            Jahrgang
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={orderBy === "gender"} direction={order} onClick={() => handleSort("gender")}>
                            Geschlecht
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Wettkampfklasse</TableCell>
                        <TableCell>Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                    {sortedRegisteredAthletes.map((reg) => (
                        <TableRow key={reg._id}>
                          <TableCell>
                            <Typography color={reg.competitionGroup ? "green" : "red"}>
                              {reg.competitionGroup ? "✅" : "⚠️"}
                            </Typography>
                          </TableCell>
                          <TableCell>{reg.athlete.firstname}</TableCell>
                          <TableCell>{reg.athlete.surname}</TableCell>
                          <TableCell>{reg.athlete.age}</TableCell>
                          <TableCell>{reg.athlete.gender}</TableCell>
                          <TableCell> 
                          <Select
                              value={typeof reg.competitionGroup === "string" ? reg.competitionGroup : reg.competitionGroup?._id || ""}
                              onChange={(e) => updateCompetitionGroup(reg._id, e.target.value)}
                              disabled={registrationStatus.isClosed}
                            >
                              <MenuItem value="">Wählen...</MenuItem>
                              {competitionGroups
                                .filter(g => {
                                  const minYear = g.ageGroup?.minYear ?? 0;
                                  const maxYear = g.ageGroup?.maxYear ?? 9999;
                                  const isAgeValid = minYear <= reg.athlete.age && maxYear >= reg.athlete.age;
                                  const isGenderValid = g.gender === reg.athlete.gender || g.gender === "mixed";
                                  return isAgeValid && isGenderValid;
                                })
                                .map(group => (
                                  <MenuItem key={group._id} value={group._id}>{group.name}</MenuItem>
                                ))}
                            </Select>
                          </TableCell>
                          <TableCell>
                            <Button 
                              variant="contained" 
                              color="error" 
                              onClick={() => unregisterAthlete(reg._id)}
                              disabled={registrationStatus.isClosed}
                            >
                              Abmelden
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Verfügbare Sportler */}
                <Typography variant="h6" sx={{ mt: 3 }}>Verfügbare Sportler</Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <TableSortLabel active={orderByAvailable === "firstname"} direction={orderAvailable} onClick={() => handleSortAvailable("firstname")}>
                            Vorname
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={orderByAvailable === "surname"} direction={orderAvailable} onClick={() => handleSortAvailable("surname")}>
                            Nachname
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={orderByAvailable === "age"} direction={orderAvailable} onClick={() => handleSortAvailable("age")}>
                            Jahrgang
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>
                          <TableSortLabel active={orderByAvailable === "gender"} direction={orderAvailable} onClick={() => handleSortAvailable("gender")}>
                            Geschlecht 
                          </TableSortLabel>
                        </TableCell>
                        <TableCell>Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedAvailableAthletes.map((ath) => (
                        <TableRow key={ath._id}>
                          <TableCell>{ath.firstname}</TableCell>
                          <TableCell>{ath.surname}</TableCell>
                          <TableCell>{ath.age}</TableCell>
                          <TableCell>{ath.gender}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              {onlyBasisAvailable ? (
                                // 🆕 Nur ein Button wenn onlyBasisAvailable true ist
                                <Button 
                                  variant="contained"
                                  color="primary"
                                  onClick={() => registerAthlete(ath._id, "Basis")}
                                  disabled={competitionLimits.isBasicFull || registrationStatus.isClosed}
                                >
                                  Anmelden
                                </Button>
                              ) : (
                                // 🔹 Normale zwei Buttons wenn onlyBasisAvailable false/undefined ist - always visible but disabled when full or closed
                                <>
                                  <Button 
                                    variant="contained"
                                    color="primary"
                                    onClick={() => registerAthlete(ath._id, "Basis")}
                                    disabled={competitionLimits.isBasicFull || registrationStatus.isClosed}
                                  >
                                    Basis anmelden
                                  </Button>
                                  <Button 
                                    variant="contained"
                                    color="primary"
                                    onClick={() => registerAthlete(ath._id, "Elite")}
                                    disabled={competitionLimits.isEliteFull || registrationStatus.isClosed}
                                  >
                                    Elite anmelden
                                  </Button>
                                </>
                              )}
    
                              {/* Waitlist buttons - anpassen für onlyBasisAvailable */}
                              {!onlyBasisAvailable && competitionLimits.isBasicFull && !registrationStatus.isClosed && (
                                <Button 
                                  variant="outlined" 
                                  color="secondary"
                                  onClick={() => addToWaitlist(ath._id, "Basis")}
                                >
                                  Basis-Warteliste
                                </Button>
                              )}
                              {!onlyBasisAvailable && competitionLimits.isEliteFull && !registrationStatus.isClosed && (
                                <Button 
                                  variant="outlined" 
                                  color="secondary"
                                  onClick={() => addToWaitlist(ath._id, "Elite")}
                                >
                                  Elite-Warteliste
                                </Button>
                              )}
                              
                              {/* 🆕 Warteliste-Button für onlyBasisAvailable */}
                              {onlyBasisAvailable && competitionLimits.isBasicFull && !registrationStatus.isClosed && (
                                <Button 
                                  variant="outlined" 
                                  color="secondary"
                                  onClick={() => addToWaitlist(ath._id, "Basis")}
                                >
                                  Warteliste
                                </Button>
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {/* Waitlist tab */}
            {activeTab === "waitlist" && (
              <>
                <Tabs 
                  value={waitlistTab} 
                  onChange={(_, newValue) => setWaitlistTab(newValue)}
                  sx={{ mt: 2, mb: 2 }}
                >
                  <Tab label="Elite" value="Elite" />
                  <Tab label="Basis" value="Basis" />
                </Tabs>

                <Typography variant="h6" sx={{ mt: 3 }}>Sportler auf der {waitlistTab}-Warteliste</Typography>
                <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Vorname</TableCell>
                        <TableCell>Nachname</TableCell>
                        <TableCell>Jahrgang</TableCell>
                        <TableCell>Geschlecht</TableCell>
                        <TableCell>Wettkampfklasse</TableCell>
                        <TableCell>Hinzugefügt am</TableCell>
                        <TableCell>Aktion</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {waitlistedAthletes[waitlistTab].length > 0 ? (
                        waitlistedAthletes[waitlistTab].map((athlete) => (
                          <TableRow key={athlete.athleteId}>
                            <TableCell>{athlete.firstname}</TableCell>
                            <TableCell>{athlete.surname}</TableCell>
                            <TableCell>{athlete.age}</TableCell>
                            <TableCell>{athlete.gender}</TableCell>
                            <TableCell>{getGroupName(athlete.competitionGroup)}</TableCell>
                            <TableCell>{new Date(athlete.addedAt).toLocaleDateString('de-DE')}</TableCell>
                            <TableCell>
                              <Button 
                                variant="contained" 
                                color="error"
                                onClick={() => removeFromWaitlist(athlete.athleteId, waitlistTab)}
                                disabled={registrationStatus.isClosed}
                              >
                                Von Warteliste entfernen
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} align="center">
                            <Typography>Keine Sportler auf der {waitlistTab}-Warteliste</Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                  <Typography>
                    <strong>Hinweis zur Warteliste:</strong> Sportler auf der Warteliste können vom Wettkampforganisator
                    freigeschaltet werden, falls Plätze frei werden. Du erhältst in diesem Fall eine Benachrichtigung.
                  </Typography>
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AthleteRegistrationPage;